import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import HttpBackend from "i18next-http-backend";

export const SUPPORTED_LOCALES = [
  "en",
  "zh-Hans",
  "zh-Hant",
  "vi",
  "ja",
  "ko",
  "es",
  "fr",
  "de",
  "ar",
  "hi",
  "pt",
  "ru",
] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const RTL_LOCALES: Locale[] = ["ar"];

export const LOCALE_NAMES: Record<Locale, string> = {
  en: "English",
  "zh-Hans": "简体中文",
  "zh-Hant": "繁體中文",
  vi: "Tiếng Việt",
  ja: "日本語",
  ko: "한국어",
  es: "Español",
  fr: "Français",
  de: "Deutsch",
  ar: "العربية",
  hi: "हिन्दी",
  pt: "Português",
  ru: "Русский",
};

export const NAMESPACES = [
  "common",
  "nav",
  "market",
  "trading",
  "community",
  "advisor",
  "agent",
  "chat",
  "settings",
  "auth",
  "onboarding",
  "landing",
  "panels",
  "admin",
  "portfolio",
  "system",
] as const;

/** Languages available in the admin panel */
export const ADMIN_LOCALES = ["en", "zh-Hans", "zh-Hant", "vi"] as const;
export type AdminLocale = (typeof ADMIN_LOCALES)[number];

export const ADMIN_LOCALE_NAMES: Record<AdminLocale, string> = {
  en: "EN",
  "zh-Hans": "简",
  "zh-Hant": "繁",
  vi: "VI",
};

function normalizeLocale(raw: string | null | undefined): Locale | undefined {
  if (!raw) return undefined;
  const v = raw.trim();
  if ((SUPPORTED_LOCALES as readonly string[]).includes(v)) return v as Locale;

  const normalized = v.toLowerCase();
  if (normalized.startsWith("zh")) {
    if (/zh-(tw|hk|mo|hant)/i.test(v)) return "zh-Hant";
    return "zh-Hans";
  }
  const primary = normalized.split(/[-_]/)[0];
  const hit = (SUPPORTED_LOCALES as readonly string[]).find(
    (l) => l.toLowerCase() === primary
  );
  return hit as Locale;
}

const customDetector = new LanguageDetector();
customDetector.addDetector({
  name: "customDetector",
  lookup() {
    if (typeof window === "undefined") return undefined;
    const local = localStorage.getItem("xfincompass-locale");
    if (local) {
      const normalized = normalizeLocale(local);
      if (normalized) return normalized;
    }
    // Auto-detect prioritizes Chinese only. A Vietnamese — or any non-Chinese /
    // unresolvable — browser defaults to English. Vietnamese stays available but
    // only when the admin explicitly picks it (persisted in localStorage above).
    const browser = navigator.language || (navigator.languages && navigator.languages[0]);
    const detected = normalizeLocale(browser);
    return detected === "zh-Hans" || detected === "zh-Hant" ? detected : "en";
  },
  cacheUserLanguage(lng) {
    if (typeof window !== "undefined") {
      const normalized = normalizeLocale(lng) || "en";
      localStorage.setItem("xfincompass-locale", normalized);
    }
  },
});

i18n
  .use(HttpBackend)
  .use(customDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: {
      // Prioritize Chinese: a key missing in one Chinese script falls back to the
      // other Chinese script before English, so Chinese never drops to English
      // while any Chinese copy exists.
      "zh-Hans": ["zh-Hant", "en"],
      "zh-Hant": ["zh-Hans", "en"],
      "zh-CN": ["zh-Hans", "zh-Hant", "en"],
      "zh-SG": ["zh-Hans", "zh-Hant", "en"],
      "zh-MY": ["zh-Hans", "zh-Hant", "en"],
      "zh-TW": ["zh-Hant", "zh-Hans", "en"],
      "zh-HK": ["zh-Hant", "zh-Hans", "en"],
      "zh-MO": ["zh-Hant", "zh-Hans", "en"],
      zh: ["zh-Hans", "zh-Hant", "en"],
      // Everything else — including Vietnamese — resolves to English for a missing
      // key. Vietnamese is never a fallback target for another language.
      default: ["en"],
    },
    supportedLngs: [...SUPPORTED_LOCALES],
    nonExplicitSupportedLngs: false,
    ns: [...NAMESPACES],
    defaultNS: "common",
    backend: {
      loadPath: "/locales/{{lng}}/{{ns}}.json",
    },
    detection: {
      order: ["customDetector"],
      caches: ["localStorage"],
      lookupLocalStorage: "xfincompass-locale",
    },
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
      bindI18n: "languageChanged loaded",
      bindI18nStore: "added removed",
    },
  });

/**
 * Normalize an i18n language code to the form the backend accepts
 * (4 locales: "en" | "zh-Hans" | "zh-Hant" | "vi"). Anything else falls
 * back to English. Use this when sending `lang=` query/header to BE.
 */
export function backendLang(raw: string | undefined): "en" | "zh-Hans" | "zh-Hant" | "vi" {
  const v = raw ?? "en";
  if (v === "zh-Hans" || v === "zh-Hant" || v === "en" || v === "vi") return v;
  if (/^zh-(tw|hk|mo|hant)/i.test(v)) return "zh-Hant";
  if (/^zh/i.test(v)) return "zh-Hans";
  const primary = v.toLowerCase().split(/[-_]/)[0];
  return primary === "vi" ? "vi" : "en";
}

export default i18n;
